import { ChevronDown, ChevronUp, Edit2, Eye, EyeOff, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { Customization, ResumeData } from '../../types';
import './EditorSidebar.css';

interface EditorSidebarProps {
  resumeData: ResumeData;
  customization: Customization;
  onDataChange: (data: ResumeData) => void;
  onCustomizationChange: (customization: Customization) => void;
}

const EditorSidebar: React.FC<EditorSidebarProps> = ({
  resumeData,
  customization,
  onDataChange,
  onCustomizationChange,
}) => {
  const [activeSection, setActiveSection] = useState<string | null>('personal');
  
  const sections = [
    { id: 'personal', label: 'Personal Information', icon: <Edit2 size={16} /> },
    { id: 'summary', label: 'Professional Summary', icon: <Edit2 size={16} /> },
    { id: 'experience', label: 'Work Experience', icon: <Edit2 size={16} /> },
    { id: 'projects', label: 'Projects', icon: <Edit2 size={16} /> },
    { id: 'education', label: 'Education', icon: <Edit2 size={16} /> },
    { id: 'skills', label: 'Skills', icon: <Edit2 size={16} /> },
    { id: 'certifications', label: 'Certifications', icon: <Edit2 size={16} /> },
  ];

  const toggleSection = (sectionId: string) => {
    setActiveSection(activeSection === sectionId ? null : sectionId);
  };

  const toggleSectionVisibility = (sectionId: string) => {
    const hidden = customization.hiddenSections || [];
    const newHidden = hidden.includes(sectionId)
      ? hidden.filter(s => s !== sectionId)
      : [...hidden, sectionId];
    
    onCustomizationChange({
      ...customization,
      hiddenSections: newHidden,
    });
  };

  const isSectionHidden = (sectionId: string) => {
    return (customization.hiddenSections || []).includes(sectionId);
  };

  // Personal Information Editing
  const renderPersonalSection = () => {
    if (activeSection !== 'personal') return null;

    return (
      <div className="section-content">
        <div className="form-group">
          <label>Full Name</label>
          <input
            type="text"
            value={resumeData.personal?.name || ''}
            onChange={(e) => onDataChange({
              ...resumeData,
              personal: { ...resumeData.personal, name: e.target.value },
            })}
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            value={resumeData.personal?.email || ''}
            onChange={(e) => onDataChange({
              ...resumeData,
              personal: { ...resumeData.personal, email: e.target.value },
            })}
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label>Phone</label>
          <input
            type="tel"
            value={resumeData.personal?.phone || ''}
            onChange={(e) => onDataChange({
              ...resumeData,
              personal: { ...resumeData.personal, phone: e.target.value },
            })}
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label>Location</label>
          <input
            type="text"
            value={resumeData.personal?.location || ''}
            onChange={(e) => onDataChange({
              ...resumeData,
              personal: { ...resumeData.personal, location: e.target.value },
            })}
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label>LinkedIn <span className="optional-hint">(optional)</span></label>
          <input
            type="text"
            value={resumeData.personal?.linkedin || ''}
            onChange={(e) => onDataChange({
              ...resumeData,
              personal: { ...resumeData.personal, linkedin: e.target.value },
            })}
            className="form-input"
            placeholder="linkedin.com/in/username or username"
          />
        </div>

        <div className="form-group">
          <label>GitHub <span className="optional-hint">(optional)</span></label>
          <input
            type="text"
            value={resumeData.personal?.github || ''}
            onChange={(e) => onDataChange({
              ...resumeData,
              personal: { ...resumeData.personal, github: e.target.value },
            })}
            className="form-input"
            placeholder="github.com/username or username"
          />
        </div>

        <div className="form-group">
          <label>Personal Website <span className="optional-hint">(optional)</span></label>
          <input
            type="text"
            value={resumeData.personal?.portfolio || ''}
            onChange={(e) => onDataChange({
              ...resumeData,
              personal: { ...resumeData.personal, portfolio: e.target.value },
            })}
            className="form-input"
            placeholder="https://yourwebsite.com"
          />
        </div>
      </div>
    );
  };

  // Summary Editing
  const renderSummarySection = () => {
    if (activeSection !== 'summary') return null;

    return (
      <div className="section-content">
        <div className="form-group">
          <label>Professional Summary</label>
          <textarea
            value={resumeData.summary || ''}
            onChange={(e) => onDataChange({
              ...resumeData,
              summary: e.target.value,
            })}
            className="form-textarea"
            rows={6}
            placeholder="Write a brief professional summary..."
          />
        </div>
      </div>
    );
  };

  // Skills Editing
  const renderSkillsSection = () => {
    if (activeSection !== 'skills') return null;

    return (
      <div className="section-content">
        <div className="form-group">
          <label>Skills (comma-separated)</label>
          <textarea
            key={`skills-${resumeData.skills?.length || 0}`}
            defaultValue={resumeData.skills?.join(', ') || ''}
            onBlur={(e) => {
              const skills = e.target.value
                .split(',')
                .map(s => s.trim())
                .filter(s => s !== '');
              onDataChange({
                ...resumeData,
                skills: skills,
              });
            }}
            className="form-textarea"
            rows={4}
            placeholder="React, TypeScript, Node.js, Python..."
          />
          <p className="form-hint">
            Type all skills, then click outside to save
          </p>
        </div>
      </div>
    );
  };

  const addNewEducation = () => {
    const newEdu = {
      institution: '',
      degree: '',
      field: '',
      startDate: '',
      endDate: '',
    };
    onDataChange({
      ...resumeData,
      education: [...(resumeData.education || []), newEdu],
    });
  };

  const removeEducation = (index: number) => {
    const newEducation = [...(resumeData.education || [])];
    newEducation.splice(index, 1);
    onDataChange({ ...resumeData, education: newEducation });
  };

  const renderEducationSection = () => {
    if (activeSection !== 'education') return null;

    return (
      <div className="section-content">
        {(!resumeData.education || resumeData.education.length === 0) && (
          <p className="empty-state">No education added yet.</p>
        )}
        {resumeData.education?.map((edu, index) => (
          <div key={index} className="item-card">
            <div className="item-card-header">
              <span className="item-number">Education {index + 1}</span>
              <button onClick={() => removeEducation(index)} className="btn-icon btn-danger" title="Remove">
                <Trash2 size={14} />
              </button>
            </div>
            <div className="form-group">
              <label>Degree</label>
              <input
                type="text"
                value={edu.degree || ''}
                onChange={(e) => {
                  const newEducation = [...(resumeData.education || [])];
                  newEducation[index] = { ...newEducation[index], degree: e.target.value };
                  onDataChange({ ...resumeData, education: newEducation });
                }}
                placeholder="Bachelor of Science"
              />
            </div>
            <div className="form-group">
              <label>Field of Study</label>
              <input
                type="text"
                value={edu.field || ''}
                onChange={(e) => {
                  const newEducation = [...(resumeData.education || [])];
                  newEducation[index] = { ...newEducation[index], field: e.target.value };
                  onDataChange({ ...resumeData, education: newEducation });
                }}
                placeholder="Computer Science"
              />
            </div>
            <div className="form-group">
              <label>Institution</label>
              <input
                type="text"
                value={edu.institution || ''}
                onChange={(e) => {
                  const newEducation = [...(resumeData.education || [])];
                  newEducation[index] = { ...newEducation[index], institution: e.target.value };
                  onDataChange({ ...resumeData, education: newEducation });
                }}
                placeholder="University Name"
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="text"
                  value={edu.startDate || ''}
                  onChange={(e) => {
                    const newEducation = [...(resumeData.education || [])];
                    newEducation[index] = { ...newEducation[index], startDate: e.target.value };
                    onDataChange({ ...resumeData, education: newEducation });
                  }}
                  placeholder="2015"
                />
              </div>
              <div className="form-group">
                <label>End Date</label>
                <input
                  type="text"
                  value={edu.endDate || ''}
                  onChange={(e) => {
                    const newEducation = [...(resumeData.education || [])];
                    newEducation[index] = { ...newEducation[index], endDate: e.target.value };
                    onDataChange({ ...resumeData, education: newEducation });
                  }}
                  placeholder="2019"
                />
              </div>
            </div>
          </div>
        ))}
        <button onClick={addNewEducation} className="btn btn-secondary btn-add-item">
          <Plus size={16} />
          Add Education
        </button>
      </div>
    );
  };

  const addNewExperience = () => {
    const newExp = {
      company: '',
      position: '',
      startDate: '',
      endDate: '',
      description: '',
      achievements: [],
    };
    onDataChange({
      ...resumeData,
      employment: [...(resumeData.employment || []), newExp],
    });
  };

  const removeExperience = (index: number) => {
    const newEmployment = [...(resumeData.employment || [])];
    newEmployment.splice(index, 1);
    onDataChange({ ...resumeData, employment: newEmployment });
  };

  const renderExperienceSection = () => {
    if (activeSection !== 'experience') return null;

    return (
      <div className="section-content">
        {(!resumeData.employment || resumeData.employment.length === 0) && (
          <p className="empty-state">No work experience added yet.</p>
        )}
        {resumeData.employment?.map((emp, index) => (
          <div key={index} className="item-card">
            <div className="item-card-header">
              <span className="item-number">Experience {index + 1}</span>
              <button onClick={() => removeExperience(index)} className="btn-icon btn-danger" title="Remove">
                <Trash2 size={14} />
              </button>
            </div>
            <div className="form-group">
              <label>Position</label>
              <input
                type="text"
                value={emp.position || ''}
                onChange={(e) => {
                  const newEmployment = [...(resumeData.employment || [])];
                  newEmployment[index] = { ...newEmployment[index], position: e.target.value };
                  onDataChange({ ...resumeData, employment: newEmployment });
                }}
                placeholder="Senior Software Engineer"
              />
            </div>
            <div className="form-group">
              <label>Company</label>
              <input
                type="text"
                value={emp.company || ''}
                onChange={(e) => {
                  const newEmployment = [...(resumeData.employment || [])];
                  newEmployment[index] = { ...newEmployment[index], company: e.target.value };
                  onDataChange({ ...resumeData, employment: newEmployment });
                }}
                placeholder="Company Name"
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="text"
                  value={emp.startDate || ''}
                  onChange={(e) => {
                    const newEmployment = [...(resumeData.employment || [])];
                    newEmployment[index] = { ...newEmployment[index], startDate: e.target.value };
                    onDataChange({ ...resumeData, employment: newEmployment });
                  }}
                  placeholder="2020"
                />
              </div>
              <div className="form-group">
                <label>End Date</label>
                <input
                  type="text"
                  value={emp.endDate || ''}
                  onChange={(e) => {
                    const newEmployment = [...(resumeData.employment || [])];
                    newEmployment[index] = { ...newEmployment[index], endDate: e.target.value };
                    onDataChange({ ...resumeData, employment: newEmployment });
                  }}
                  placeholder="Present"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={emp.description || ''}
                onChange={(e) => {
                  const newEmployment = [...(resumeData.employment || [])];
                  newEmployment[index] = { ...newEmployment[index], description: e.target.value };
                  onDataChange({ ...resumeData, employment: newEmployment });
                }}
                rows={3}
                placeholder="Brief overview of role..."
              />
            </div>
          </div>
        ))}
        <button onClick={addNewExperience} className="btn btn-secondary btn-add-item">
          <Plus size={16} />
          Add Experience
        </button>
      </div>
    );
  };

  const addNewProject = () => {
    const newProject = {
      name: '',
      description: '',
      technologies: [],
      highlights: [],
    };
    onDataChange({
      ...resumeData,
      projects: [...(resumeData.projects || []), newProject],
    });
  };

  const removeProject = (index: number) => {
    const newProjects = [...(resumeData.projects || [])];
    newProjects.splice(index, 1);
    onDataChange({ ...resumeData, projects: newProjects });
  };

  const renderProjectsSection = () => {
    if (activeSection !== 'projects') return null;

    return (
      <div className="section-content">
        {(!resumeData.projects || resumeData.projects.length === 0) && (
          <p className="empty-state">No projects added yet.</p>
        )}
        {resumeData.projects?.map((proj, index) => (
          <div key={index} className="item-card">
            <div className="item-card-header">
              <span className="item-number">Project {index + 1}</span>
              <button onClick={() => removeProject(index)} className="btn-icon btn-danger" title="Remove">
                <Trash2 size={14} />
              </button>
            </div>
            <div className="form-group">
              <label>Project Name</label>
              <input
                type="text"
                value={proj.name || ''}
                onChange={(e) => {
                  const newProjects = [...(resumeData.projects || [])];
                  newProjects[index] = { ...newProjects[index], name: e.target.value };
                  onDataChange({ ...resumeData, projects: newProjects });
                }}
                placeholder="Project Name"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={proj.description || ''}
                onChange={(e) => {
                  const newProjects = [...(resumeData.projects || [])];
                  newProjects[index] = { ...newProjects[index], description: e.target.value };
                  onDataChange({ ...resumeData, projects: newProjects });
                }}
                rows={3}
                placeholder="Project description..."
              />
            </div>
            <div className="form-group">
              <label>Technologies (comma-separated)</label>
              <input
                type="text"
                defaultValue={proj.technologies?.join(', ') || ''}
                onBlur={(e) => {
                  const newProjects = [...(resumeData.projects || [])];
                  const technologies = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                  newProjects[index] = { ...newProjects[index], technologies };
                  onDataChange({ ...resumeData, projects: newProjects });
                }}
                placeholder="React, Node.js, MongoDB..."
              />
              <p className="form-hint">Type all technologies, then click outside to save</p>
            </div>
          </div>
        ))}
        <button onClick={addNewProject} className="btn btn-secondary btn-add-item">
          <Plus size={16} />
          Add Project
        </button>
      </div>
    );
  };

  const addNewCertification = () => {
    const newCert = {
      name: '',
      issuer: '',
      issueDate: '',
    };
    onDataChange({
      ...resumeData,
      certifications: [...(resumeData.certifications || []), newCert],
    });
  };

  const removeCertification = (index: number) => {
    const newCerts = [...(resumeData.certifications || [])];
    newCerts.splice(index, 1);
    onDataChange({ ...resumeData, certifications: newCerts });
  };

  const renderCertificationsSection = () => {
    if (activeSection !== 'certifications') return null;

    return (
      <div className="section-content">
        {(!resumeData.certifications || resumeData.certifications.length === 0) && (
          <p className="empty-state">No certifications added yet.</p>
        )}
        {resumeData.certifications?.map((cert, index) => (
          <div key={index} className="item-card">
            <div className="item-card-header">
              <span className="item-number">Certification {index + 1}</span>
              <button onClick={() => removeCertification(index)} className="btn-icon btn-danger" title="Remove">
                <Trash2 size={14} />
              </button>
            </div>
            <div className="form-group">
              <label>Certification Name</label>
              <input
                type="text"
                value={cert.name || ''}
                onChange={(e) => {
                  const newCerts = [...(resumeData.certifications || [])];
                  newCerts[index] = { ...newCerts[index], name: e.target.value };
                  onDataChange({ ...resumeData, certifications: newCerts });
                }}
                placeholder="AWS Certified Solutions Architect"
              />
            </div>
            <div className="form-group">
              <label>Issuing Organization</label>
              <input
                type="text"
                value={cert.issuer || ''}
                onChange={(e) => {
                  const newCerts = [...(resumeData.certifications || [])];
                  newCerts[index] = { ...newCerts[index], issuer: e.target.value };
                  onDataChange({ ...resumeData, certifications: newCerts });
                }}
                placeholder="Amazon Web Services"
              />
            </div>
            <div className="form-group">
              <label>Issue Date</label>
              <input
                type="text"
                value={cert.issueDate || ''}
                onChange={(e) => {
                  const newCerts = [...(resumeData.certifications || [])];
                  newCerts[index] = { ...newCerts[index], issueDate: e.target.value };
                  onDataChange({ ...resumeData, certifications: newCerts });
                }}
                placeholder="2022"
              />
            </div>
          </div>
        ))}
        <button onClick={addNewCertification} className="btn btn-secondary btn-add-item">
          <Plus size={16} />
          Add Certification
        </button>
      </div>
    );
  };

  return (
    <div className="editor-sidebar-content">
      <div className="sidebar-header">
        <h2>Edit Content</h2>
        <p className="text-secondary">Make changes to your resume content</p>
      </div>

      <div className="sections-list">
        {sections.map((section) => (
          <div key={section.id} className="section-item">
            <div className="section-header">
              <button
                onClick={() => toggleSection(section.id)}
                className="section-toggle"
              >
                <span className="section-info">
                  {section.icon}
                  <span className="section-label">{section.label}</span>
                </span>
                {activeSection === section.id ? (
                  <ChevronUp size={16} />
                ) : (
                  <ChevronDown size={16} />
                )}
              </button>
              
              {section.id !== 'personal' && (
                <button
                  onClick={() => toggleSectionVisibility(section.id)}
                  className="visibility-toggle"
                  title={isSectionHidden(section.id) ? 'Show section' : 'Hide section'}
                >
                  {isSectionHidden(section.id) ? (
                    <EyeOff size={16} className="text-error" />
                  ) : (
                    <Eye size={16} className="text-success" />
                  )}
                </button>
              )}
            </div>

            {section.id === 'personal' && renderPersonalSection()}
            {section.id === 'summary' && renderSummarySection()}
            {section.id === 'education' && renderEducationSection()}
            {section.id === 'experience' && renderExperienceSection()}
            {section.id === 'projects' && renderProjectsSection()}
            {section.id === 'skills' && renderSkillsSection()}
            {section.id === 'certifications' && renderCertificationsSection()}
          </div>
        ))}
      </div>
    </div>
  );
};

export default EditorSidebar;

